<template>
  <div class="page services-page">
    <div class="page-header">
      <div class="page-header-row">
        <div>
          <h1 class="page-title">Serviços</h1>
          <p class="page-subtitle">Catálogo de serviços e preços</p>
        </div>
        <button
          type="button"
          class="btn btn-secondary btn-reorder"
          @click="openReorder"
        >
          <i class="material-icons">swap_vert</i>
          Reordenar
        </button>
      </div>
    </div>

    <div class="services-search-wrap">
      <label for="service-search" class="services-search-label">Filtrar</label>
      <input
        id="service-search"
        v-model="nameFilter"
        type="search"
        class="services-search-input"
        placeholder="Nome do serviço"
        aria-label="Filtrar por nome"
        autocomplete="off"
      />
      <i class="material-icons services-search-icon" aria-hidden="true">search</i>
    </div>

    <div class="card-list">
      <article
        v-for="service in services"
        :key="service.id"
        class="card card--with-icon"
      >
        <div class="card-icon-wrap services-card-icon">
          <img
            v-if="service.imageUrl"
            :src="service.imageUrl"
            :alt="service.name"
            class="services-card-img"
          />
          <i v-else class="material-icons">spa</i>
        </div>
        <div class="card-content">
          <h3 class="card-title card-primary">{{ service.name }}</h3>
          <p v-if="service.group" class="card-meta card-secondary">
            <i class="material-icons card-meta-icon" style="font-size: 1rem;">category</i>
            {{ service.group }}
          </p>
          <p v-if="service.description" class="card-meta card-secondary services-card-desc">
            {{ service.description }}
          </p>
          <p class="card-meta card-accent">
            <i class="material-icons card-meta-icon">payments</i>
            {{ Number(service.price).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) }}
          </p>
          <span v-if="service.isPublic" class="card-badge">
            <i class="material-icons">public</i>
            Público
          </span>
        </div>
        <div class="card-actions">
          <button
            type="button"
            class="btn-icon"
            aria-label="Editar"
            @click="handleEditClick(service.id)"
          >
            <i class="material-icons">edit</i>
          </button>
          <button
            type="button"
            class="btn-icon btn-icon-danger"
            aria-label="Excluir"
            @click="handleDeleteClick(service.id)"
          >
            <i class="material-icons">delete_outline</i>
          </button>
        </div>
      </article>
    </div>

    <button
      type="button"
      class="btn-fab"
      @click="handleAddClick"
      aria-label="Novo serviço"
    >
      <i class="material-icons">add</i>
    </button>

    <Drawer :model-value="showModal" @update:model-value="showModal = $event">
      <div class="modal-header">
        <h2 class="modal-title">{{ selectedId ? 'Alterar serviço' : 'Novo serviço' }}</h2>
        <button
          type="button"
          class="modal-close"
          aria-label="Fechar"
          @click="showModal = false"
        >
          <i class="material-icons">close</i>
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="save">
          <div class="form-group">
            <label for="name">Nome</label>
            <input
              id="name"
              type="text"
              name="name"
              v-model="name"
              :aria-invalid="!!nameError"
              aria-describedby="name-error"
            />
            <p v-if="nameError" id="name-error" class="field-error" role="alert">{{ nameError }}</p>
          </div>
          <div class="form-group">
            <label for="group">Grupo (opcional)</label>
            <input
              id="group"
              type="text"
              v-model="group"
              list="group-datalist"
              placeholder="Ex: Extensão de Cílios, Manutenção, Remoção..."
              autocomplete="off"
            />
            <datalist id="group-datalist">
              <option v-for="g in existingGroupNames" :key="g" :value="g" />
            </datalist>
            <p class="field-hint">Selecione um grupo existente ou digite um novo</p>
          </div>
          <div class="form-group">
            <label for="price">Preço</label>
            <CurrencyInput
              id="price"
              :model-value="price"
              @update:model-value="price = $event"
              placeholder="0,00"
              aria-label="Preço"
              :invalid="!!priceError"
            />
            <p v-if="priceError" id="price-error" class="field-error" role="alert">{{ priceError }}</p>
          </div>
          <div class="form-group">
            <label for="slotsRequired">Duração (slots de 30 min)</label>
            <input
              id="slotsRequired"
              type="number"
              v-model.number="slotsRequired"
              min="1"
              step="1"
              aria-label="Slots de 30 minutos"
              :aria-invalid="!!slotsError"
            />
            <p v-if="slotsError" class="field-error" role="alert">{{ slotsError }}</p>
            <p class="field-hint">Ex: 2 = 1 hora, 3 = 1h30. Mínimo 1.</p>
          </div>
          <div class="form-group form-group--checkbox">
            <label class="checkbox-label">
              <input type="checkbox" v-model="isPublic" />
              <span>Exibir na página pública</span>
            </label>
          </div>
          <div class="form-group">
            <label for="description">Descrição</label>
            <textarea
              id="description"
              v-model="description"
              rows="2"
              placeholder="Breve descrição do serviço"
              :disabled="!isPublic"
              :aria-disabled="!isPublic"
            />
          </div>
          <div class="form-group">
            <label for="imageUrl">URL da imagem</label>
            <input
              id="imageUrl"
              type="url"
              v-model="imageUrl"
              placeholder="https://…"
              :disabled="!isPublic"
              :aria-disabled="!isPublic"
            />
          </div>

          <div class="form-group supply-config-section">
            <h3 class="supply-config-title">Insumos do serviço</h3>
            <p class="field-hint">Insumos de quantidade fixa e grupos de containers (bandejas, frascos).</p>
            <p v-if="supplyConfigError" class="field-error" role="alert">{{ supplyConfigError }}</p>

            <h4 class="supply-config-subtitle">Quantidade fixa</h4>
            <div v-for="(row, idx) in supplyQuantityRows" :key="'qty-' + idx" class="supply-config-row">
              <v-select
                :options="quantitySupplies"
                label="name"
                :reduce="(supply) => supply.id"
                v-model="row.supplyId"
                placeholder="Selecione o insumo"
                :clearable="false"
                :class="{ 'field-invalid': quantityRowErrors[idx] }"
                class="supply-config-select"
              />
              <input v-model.number="row.defaultQuantity" type="number" min="0" step="1" class="supply-config-qty" />
              <button type="button" class="btn-icon btn-icon-danger" @click="removeQuantityRow(idx)">
                <i class="material-icons">delete_outline</i>
              </button>
              <p v-if="quantityRowErrors[idx]" class="field-error supply-row-error">{{ quantityRowErrors[idx] }}</p>
            </div>
            <button type="button" class="btn btn-secondary btn-sm" @click="addQuantityRow">+ Quantidade</button>

            <h4 class="supply-config-subtitle">Grupos de containers</h4>
            <div v-for="(grp, gIdx) in supplyContainerGroups" :key="grp.groupId" class="supply-group-config">
              <div class="supply-group-header">
                <span class="supply-group-label">Grupo {{ gIdx + 1 }}</span>
                <button type="button" class="btn-icon btn-icon-danger" @click="removeContainerGroup(gIdx)">
                  <i class="material-icons">delete_outline</i>
                </button>
              </div>
              <div class="field">
                <input
                  v-model="grp.groupLabel"
                  type="text"
                  placeholder="Nome do grupo (ex: Cílios)"
                  :aria-invalid="!!containerGroupErrors[gIdx]?.label"
                />
                <p v-if="containerGroupErrors[gIdx]?.label" class="field-error">{{ containerGroupErrors[gIdx].label }}</p>
              </div>
              <div class="field">
                <v-select
                  multiple
                  :options="containerSupplies"
                  label="name"
                  :reduce="(supply) => supply.id"
                  v-model="grp.allowedSupplyIds"
                  placeholder="Selecione os insumos"
                  :class="{ 'field-invalid': containerGroupErrors[gIdx]?.supplies }"
                />
                <p v-if="containerGroupErrors[gIdx]?.supplies" class="field-error">{{ containerGroupErrors[gIdx].supplies }}</p>
              </div>
            </div>
            <button type="button" class="btn btn-secondary btn-sm" @click="addContainerGroup">+ Grupo</button>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" :disabled="saveLoading" @click="showModal = false">
          Cancelar
        </button>
        <button type="button" class="btn btn-primary" :disabled="saveLoading" @click="save">
          {{ saveLoading ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
    </Drawer>

    <Drawer :model-value="showReorder" @update:model-value="showReorder = $event">
      <div class="modal-header">
        <h2 class="modal-title">Reordenar</h2>
        <button
          type="button"
          class="modal-close"
          aria-label="Fechar"
          @click="showReorder = false"
        >
          <i class="material-icons">close</i>
        </button>
      </div>
      <div class="modal-body reorder-body">
        <p v-if="reorderGroupList.length === 0" class="reorder-empty">
          Nenhum serviço público para reordenar.
        </p>
        <template v-else>
          <p class="reorder-hint">
            Arraste para reordenar ou use as setas. A ordem reflete na página pública.
          </p>
          <draggable
            :list="reorderGroupList"
            item-key="name"
            handle=".reorder-group-handle"
            ghost-class="reorder-ghost"
            :animation="200"
          >
            <template #item="{ element: grp, index: gIdx }">
              <div class="reorder-group">
                <div class="reorder-group-header">
                  <button
                    type="button"
                    class="reorder-group-handle"
                    aria-label="Arrastar grupo"
                  >
                    <i class="material-icons">drag_indicator</i>
                  </button>
                  <h3 class="reorder-group-title">{{ grp.name }}</h3>
                  <div class="reorder-arrows">
                    <button
                      type="button"
                      class="btn-icon btn-icon-sm"
                      :disabled="gIdx === 0"
                      aria-label="Mover grupo para cima"
                      @click="moveGroupUp(gIdx)"
                    >
                      <i class="material-icons">arrow_upward</i>
                    </button>
                    <button
                      type="button"
                      class="btn-icon btn-icon-sm"
                      :disabled="gIdx === reorderGroupList.length - 1"
                      aria-label="Mover grupo para baixo"
                      @click="moveGroupDown(gIdx)"
                    >
                      <i class="material-icons">arrow_downward</i>
                    </button>
                  </div>
                </div>
                <draggable
                  :list="grp.services"
                  item-key="id"
                  handle=".reorder-handle"
                  ghost-class="reorder-ghost"
                  :animation="200"
                >
                  <template #item="{ element: svc, index: sIdx }">
                    <div class="reorder-item">
                      <button
                        type="button"
                        class="reorder-handle"
                        aria-label="Arrastar serviço"
                      >
                        <i class="material-icons">drag_indicator</i>
                      </button>
                      <span class="reorder-item-name">{{ svc.name }}</span>
                      <span class="reorder-item-price">
                        {{ Number(svc.price).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) }}
                      </span>
                      <div class="reorder-arrows">
                        <button
                          type="button"
                          class="btn-icon btn-icon-sm"
                          :disabled="sIdx === 0"
                          aria-label="Mover para cima"
                          @click="moveServiceUp(gIdx, sIdx)"
                        >
                          <i class="material-icons">arrow_upward</i>
                        </button>
                        <button
                          type="button"
                          class="btn-icon btn-icon-sm"
                          :disabled="sIdx === grp.services.length - 1"
                          aria-label="Mover para baixo"
                          @click="moveServiceDown(gIdx, sIdx)"
                        >
                          <i class="material-icons">arrow_downward</i>
                        </button>
                      </div>
                    </div>
                  </template>
                </draggable>
              </div>
            </template>
          </draggable>
        </template>
      </div>
      <div v-if="reorderGroupList.length > 0" class="modal-footer">
        <button
          type="button"
          class="btn btn-secondary"
          :disabled="reorderLoading"
          @click="showReorder = false"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="reorderLoading"
          @click="saveOrder"
        >
          {{ reorderLoading ? 'Salvando...' : 'Salvar ordem' }}
        </button>
      </div>
    </Drawer>

    <ConfirmDialog
      v-model="showDeleteConfirm"
      title="Excluir serviço"
      message="Tem certeza que deseja excluir este serviço? Esta ação não pode ser desfeita."
      confirm-label="Excluir"
      :confirm-loading="deleteLoading"
      @confirm="doDeleteService"
    />
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import Drawer from '@/components/Drawer.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import CurrencyInput from '@/components/CurrencyInput.vue';
import { doc, collection, deleteDoc, setDoc, addDoc, writeBatch } from 'firebase/firestore';
import { firestore } from '@/firebase';
import { ref, computed, watch } from 'vue';
import { useServiceStore } from '@/stores/service';
import { useSupplyStore } from '@/stores/supply';
import { useToast } from '@/composables/useToast';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

export default {
  name: 'Service',
  components: { draggable, Drawer, ConfirmDialog, CurrencyInput, vSelect },
  setup() {
    const serviceStore = useServiceStore();
    const supplyStore = useSupplyStore();
    const { show: showToast } = useToast();
    const showModal = ref(false);
    const showDeleteConfirm = ref(false);
    const deleteTargetId = ref(null);
    const saveLoading = ref(false);
    const deleteLoading = ref(false);
    const nameError = ref('');
    const priceError = ref('');
    const slotsError = ref('');
    const selectedId = ref(null);
    const name = ref('');
    const group = ref('');
    const description = ref('');
    const price = ref(null);
    const imageUrl = ref('');
    const isPublic = ref(false);
    const slotsRequired = ref(2);
    const supplyQuantityRows = ref([]);
    const supplyContainerGroups = ref([]);
    const supplyConfigError = ref('');
    const quantityRowErrors = ref([]);
    const containerGroupErrors = ref([]);

    const allSupplies = computed(() => supplyStore.allSupplies);
    const quantitySupplies = computed(() =>
      supplyStore.activeSupplies.filter((s) => s.tracking === 'quantity')
    );
    const containerSupplies = computed(() =>
      supplyStore.activeSupplies.filter((s) => s.tracking === 'container')
    );
    const showReorder = ref(false);
    const reorderLoading = ref(false);
    const reorderGroupList = ref([]);
    const nameFilter = ref('');

    const existingGroupNames = computed(() => {
      const seen = new Set();
      for (const s of Object.values(serviceStore.services)) {
        const g = (s.group || '').trim();
        if (g && !seen.has(g)) seen.add(g);
      }
      return [...seen].sort((a, b) => a.localeCompare(b));
    });

    const nextPublicOrder = () => {
      const publicServices = Object.values(serviceStore.services).filter(s => s.isPublic);
      if (publicServices.length === 0) return 0;
      return Math.max(...publicServices.map(s => s.order ?? 0)) + 1;
    };

    const handleAddClick = () => {
      nameError.value = '';
      priceError.value = '';
      slotsError.value = '';
      selectedId.value = null;
      name.value = '';
      group.value = '';
      description.value = '';
      price.value = null;
      imageUrl.value = '';
      isPublic.value = false;
      slotsRequired.value = 2;
      supplyQuantityRows.value = [];
      supplyContainerGroups.value = [];
      clearSupplyConfigErrors();
      showModal.value = true;
    };

    const handleEditClick = (id) => {
      nameError.value = '';
      priceError.value = '';
      slotsError.value = '';
      selectedId.value = id;
      const s = serviceStore.services[id];
      name.value = s.name ?? '';
      group.value = s.group ?? '';
      description.value = s.description ?? '';
      price.value = s.price != null ? Number(s.price) : null;
      imageUrl.value = s.imageUrl ?? '';
      isPublic.value = !!s.isPublic;
      slotsRequired.value = s.slotsRequired != null && Number(s.slotsRequired) >= 1 ? Number(s.slotsRequired) : 2;
      const cfg = s.supplyConfig || {};
      supplyQuantityRows.value = Array.isArray(cfg.quantity)
        ? cfg.quantity.map((row) => ({
            supplyId: row.supplyId || '',
            defaultQuantity: Number(row.defaultQuantity) || 1,
          }))
        : [];
      supplyContainerGroups.value = Array.isArray(cfg.containers)
        ? cfg.containers.map((group, idx) => ({
            groupId: group.groupId || `group-${idx}`,
            groupLabel: group.groupLabel || '',
            allowedSupplyIds: Array.isArray(group.allowedSupplyIds) ? [...group.allowedSupplyIds] : [],
          }))
        : [];
      clearSupplyConfigErrors();
      showModal.value = true;
    };

    const addQuantityRow = () => {
      supplyQuantityRows.value.push({ supplyId: null, defaultQuantity: 1 });
    };

    const removeQuantityRow = (idx) => {
      supplyQuantityRows.value.splice(idx, 1);
    };

    const addContainerGroup = () => {
      supplyContainerGroups.value.push({
        groupId: `group-${Date.now()}`,
        groupLabel: '',
        allowedSupplyIds: [],
      });
    };

    const removeContainerGroup = (idx) => {
      supplyContainerGroups.value.splice(idx, 1);
    };

    const clearSupplyConfigErrors = () => {
      supplyConfigError.value = '';
      quantityRowErrors.value = [];
      containerGroupErrors.value = [];
    };

    const validateSupplyConfig = () => {
      clearSupplyConfigErrors();
      let valid = true;
      const qtyErrors = [];
      const seenQtyIds = new Set();

      supplyQuantityRows.value.forEach((row, idx) => {
        if (!row.supplyId) {
          qtyErrors[idx] = 'Selecione o insumo.';
          valid = false;
        } else if (seenQtyIds.has(row.supplyId)) {
          qtyErrors[idx] = 'Insumo já adicionado.';
          valid = false;
        } else {
          seenQtyIds.add(row.supplyId);
        }
      });
      quantityRowErrors.value = qtyErrors;

      const groupErrors = [];
      supplyContainerGroups.value.forEach((group, idx) => {
        const errors = {};
        if (!group.groupLabel?.trim()) {
          errors.label = 'Informe o nome do grupo.';
          valid = false;
        }
        if (!Array.isArray(group.allowedSupplyIds) || group.allowedSupplyIds.length === 0) {
          errors.supplies = 'Selecione ao menos um insumo.';
          valid = false;
        }
        if (Object.keys(errors).length > 0) {
          groupErrors[idx] = errors;
        }
      });
      containerGroupErrors.value = groupErrors;

      if (!valid) {
        supplyConfigError.value = 'Revise os insumos configurados antes de salvar.';
      }
      return valid;
    };

    const handleDeleteClick = (id) => {
      deleteTargetId.value = id;
      showDeleteConfirm.value = true;
    };

    const doDeleteService = async () => {
      if (!deleteTargetId.value) return;
      deleteLoading.value = true;
      try {
        await deleteDoc(doc(firestore, 'services', deleteTargetId.value));
        deleteTargetId.value = null;
        showDeleteConfirm.value = false;
        showToast('Serviço excluído.', 'success');
      } catch (err) {
        showToast('Não foi possível excluir. Tente novamente.', 'error');
      } finally {
        deleteLoading.value = false;
      }
    };

    watch(name, () => { nameError.value = ''; });
    watch(price, () => { priceError.value = ''; });
    watch(supplyQuantityRows, () => { clearSupplyConfigErrors(); }, { deep: true });
    watch(supplyContainerGroups, () => { clearSupplyConfigErrors(); }, { deep: true });

    const save = async () => {
      nameError.value = '';
      priceError.value = '';
      slotsError.value = '';
      let valid = true;
      if (!name.value || !name.value.trim()) {
        nameError.value = 'Informe o nome do serviço.';
        valid = false;
      }
      const priceNum = price.value != null ? Number(price.value) : null;
      if (priceNum === null || isNaN(priceNum) || priceNum < 0) {
        priceError.value = 'Informe um preço válido.';
        valid = false;
      }
      const slotsNum = slotsRequired.value != null ? Number(slotsRequired.value) : 2;
      if (!Number.isInteger(slotsNum) || slotsNum < 1) {
        slotsError.value = 'Informe pelo menos 1 slot (30 min).';
        valid = false;
      }
      if (!valid) return;
      if (!validateSupplyConfig()) return;
      saveLoading.value = true;
      try {
        const existingOrder = selectedId.value
          ? (serviceStore.services[selectedId.value]?.order ?? 0)
          : nextPublicOrder();
        const docData = {
          name: name.value.trim(),
          price: priceNum,
          group: (group.value || '').trim() || null,
          description: (description.value || '').trim() || null,
          imageUrl: (imageUrl.value || '').trim() || null,
          order: existingOrder,
          isPublic: !!isPublic.value,
          slotsRequired: slotsNum,
          supplyConfig: {
            quantity: supplyQuantityRows.value.map((row) => ({
              supplyId: row.supplyId,
              defaultQuantity: Math.max(0, Number(row.defaultQuantity) || 0),
            })),
            containers: supplyContainerGroups.value.map((g) => ({
              groupId: g.groupId,
              groupLabel: (g.groupLabel || '').trim(),
              allowedSupplyIds: g.allowedSupplyIds || [],
            })),
          },
        };
        if (selectedId.value) {
          await setDoc(doc(firestore, 'services', selectedId.value), docData, { merge: true });
        } else {
          await addDoc(collection(firestore, 'services'), docData);
        }
        showModal.value = false;
        showToast(selectedId.value ? 'Serviço alterado.' : 'Serviço salvo.', 'success');
      } catch (err) {
        showToast('Não foi possível salvar. Tente novamente.', 'error');
      } finally {
        saveLoading.value = false;
      }
    };

    const sortByName = (a, b) => {
      const x = (a.name || '').toUpperCase();
      const y = (b.name || '').toUpperCase();
      return x < y ? -1 : x > y ? 1 : 0;
    };

    const openReorder = () => {
      const allServices = Object.entries(serviceStore.services)
        .map(([id, data]) => ({ id, ...data }))
        .filter(s => s.isPublic);

      const groups = {};
      for (const s of allServices) {
        const groupName = (s.group || '').trim() || 'Outros';
        if (!groups[groupName]) groups[groupName] = [];
        groups[groupName].push(s);
      }

      for (const g in groups) {
        groups[g].sort(
          (a, b) => (a.order ?? 999) - (b.order ?? 999) || (a.name || '').localeCompare(b.name || '')
        );
      }

      const sortedGroupNames = Object.keys(groups).sort((a, b) => {
        if (a === 'Outros') return 1;
        if (b === 'Outros') return -1;
        return (groups[a][0]?.order ?? 999) - (groups[b][0]?.order ?? 999);
      });

      reorderGroupList.value = sortedGroupNames.map(gName => ({
        name: gName,
        services: groups[gName].map(s => ({ id: s.id, name: s.name, price: s.price })),
      }));

      showReorder.value = true;
    };

    const moveGroupUp = (idx) => {
      if (idx <= 0) return;
      const list = reorderGroupList.value;
      const item = list.splice(idx, 1)[0];
      list.splice(idx - 1, 0, item);
    };

    const moveGroupDown = (idx) => {
      const list = reorderGroupList.value;
      if (idx >= list.length - 1) return;
      const item = list.splice(idx, 1)[0];
      list.splice(idx + 1, 0, item);
    };

    const moveServiceUp = (groupIdx, serviceIdx) => {
      if (serviceIdx <= 0) return;
      const services = reorderGroupList.value[groupIdx].services;
      const item = services.splice(serviceIdx, 1)[0];
      services.splice(serviceIdx - 1, 0, item);
    };

    const moveServiceDown = (groupIdx, serviceIdx) => {
      const services = reorderGroupList.value[groupIdx].services;
      if (serviceIdx >= services.length - 1) return;
      const item = services.splice(serviceIdx, 1)[0];
      services.splice(serviceIdx + 1, 0, item);
    };

    const saveOrder = async () => {
      reorderLoading.value = true;
      try {
        const batch = writeBatch(firestore);
        let orderValue = 0;
        for (const grp of reorderGroupList.value) {
          for (const svc of grp.services) {
            batch.update(doc(firestore, 'services', svc.id), { order: orderValue });
            orderValue++;
          }
        }
        await batch.commit();
        showReorder.value = false;
        showToast('Ordem salva.', 'success');
      } catch (err) {
        showToast('Não foi possível salvar a ordem. Tente novamente.', 'error');
      } finally {
        reorderLoading.value = false;
      }
    };

    return {
      name,
      group,
      nameFilter,
      existingGroupNames,
      description,
      price,
      imageUrl,
      isPublic,
      slotsRequired,
      selectedId,
      showModal,
      saveLoading,
      deleteLoading,
      save,
      handleAddClick,
      handleEditClick,
      handleDeleteClick,
      doDeleteService,
      showDeleteConfirm,
      nameError,
      priceError,
      slotsError,
      showReorder,
      reorderLoading,
      reorderGroupList,
      openReorder,
      moveGroupUp,
      moveGroupDown,
      moveServiceUp,
      moveServiceDown,
      saveOrder,
      allSupplies,
      quantitySupplies,
      containerSupplies,
      supplyConfigError,
      quantityRowErrors,
      containerGroupErrors,
      supplyQuantityRows,
      supplyContainerGroups,
      addQuantityRow,
      removeQuantityRow,
      addContainerGroup,
      removeContainerGroup,
      services: computed(() => {
        const list = Object.entries(serviceStore.services).map(([id, data]) => ({ id, ...data }));
        const sorted = list.sort((a, b) => sortByName(a, b));
        const q = (nameFilter.value || '').trim().toLowerCase();
        if (!q) return sorted;
        return sorted.filter((s) => (s.name || '').toLowerCase().includes(q));
      }),
    };
  },
};
</script>

<style scoped>
.services-page {
  padding: var(--space-md) 0;
}

.page-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-md);
}

.services-search-wrap {
  position: relative;
  margin-bottom: var(--space-lg);
}

.services-search-label {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.services-search-input {
  width: 100%;
  padding: 10px 12px 10px 44px;
  font-size: 1rem;
  font-family: inherit;
  color: var(--color-text);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.services-search-input:focus {
  outline: none;
  border-color: var(--color-border-focus);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}

.services-search-input::placeholder {
  color: var(--color-text-muted);
}

.services-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 22px;
  color: var(--color-text-muted);
  pointer-events: none;
}

.btn-reorder {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  padding: 8px 14px;
  font-size: 0.8rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-reorder .material-icons {
  font-size: 18px;
}

.field-error {
  font-size: 0.875rem;
  color: var(--color-error);
  margin-top: var(--space-xs);
  margin-bottom: 0;
}

.services-card-icon {
  overflow: hidden;
}

.services-card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.services-card-desc {
  font-size: 0.85rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.form-group--checkbox {
  margin-top: var(--space-md);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  cursor: pointer;
  text-transform: none;
  font-weight: normal;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
}

.field-hint {
  font-size: 0.8rem;
  color: var(--color-text-muted);
  margin-top: var(--space-xs);
  margin-bottom: 0;
}

/* Reorder drawer */
.reorder-body {
  padding-bottom: var(--space-sm);
}

.reorder-hint {
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  margin: 0 0 var(--space-md);
}

.reorder-empty {
  font-size: 0.9rem;
  color: var(--color-text-muted);
  text-align: center;
  padding: var(--space-xl) 0;
}

.reorder-group {
  margin-bottom: var(--space-md);
}

.reorder-group:last-child {
  margin-bottom: 0;
}

.reorder-group-header {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) 0;
  border-bottom: 2px solid var(--color-accent);
  margin-bottom: var(--space-xs);
}

.reorder-group-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  color: var(--color-accent);
  cursor: grab;
  padding: 0;
  flex-shrink: 0;
  border-radius: var(--radius-sm);
  transition: background 0.15s;
}

.reorder-group-handle:active {
  cursor: grabbing;
}

.reorder-group-handle:hover {
  background: var(--color-accent-soft);
}

.reorder-group-handle .material-icons {
  font-size: 20px;
}

.reorder-group-title {
  flex: 1;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-accent);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.reorder-item {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-xs);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  margin-bottom: var(--space-xs);
  transition: box-shadow 0.15s, border-color 0.15s;
}

.reorder-item:last-child {
  margin-bottom: 0;
}

.reorder-item:hover {
  border-color: var(--color-border-focus);
}

.reorder-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  color: var(--color-text-muted);
  cursor: grab;
  padding: 0;
  flex-shrink: 0;
  border-radius: var(--radius-sm);
  transition: color 0.15s, background 0.15s;
}

.reorder-handle:active {
  cursor: grabbing;
}

.reorder-handle:hover {
  color: var(--color-text);
  background: var(--color-bg);
}

.reorder-handle .material-icons {
  font-size: 20px;
}

.reorder-item-name {
  flex: 1;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--color-text);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reorder-item-price {
  font-size: 0.8rem;
  color: var(--color-text-muted);
  white-space: nowrap;
  flex-shrink: 0;
}

.reorder-arrows {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex-shrink: 0;
}

.btn-icon-sm {
  width: 26px;
  height: 26px;
}

.btn-icon-sm .material-icons {
  font-size: 16px;
}

.reorder-ghost {
  opacity: 0.4;
  background: var(--color-accent-soft);
  border-color: var(--color-accent);
}

.supply-config-section {
  margin-top: var(--space-lg);
  padding-top: var(--space-md);
  border-top: 1px solid var(--color-border);
}

.supply-config-title {
  font-size: 1rem;
  margin: 0 0 var(--space-sm);
}

.supply-config-subtitle {
  font-size: 0.9rem;
  margin: var(--space-md) 0 var(--space-sm);
}

.supply-config-row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
  align-items: center;
  margin-bottom: var(--space-sm);
}

.supply-group-config {
  margin-bottom: var(--space-md);
  padding: var(--space-md);
  background: var(--color-accent-soft);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
}

.supply-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-sm);
}

.supply-group-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.supply-group-config .field {
  margin-bottom: var(--space-sm);
}

.supply-group-config .field:last-child {
  margin-bottom: 0;
}

.supply-group-config :deep(.v-select) {
  width: 100%;
}

.supply-config-qty {
  width: 5rem;
}

.supply-config-select {
  flex: 1;
  min-width: 0;
}

.supply-row-error {
  flex: 1 1 100%;
  margin: 0;
}

.field-invalid :deep(.v-select .vs__dropdown-toggle) {
  border-color: var(--color-error);
}

.btn-sm {
  font-size: 0.8rem;
  padding: 6px 10px;
}
</style>
